// types/AsyncExecution.ts - Consolidated async execution types

// =================== Core Types ===================
export type ExecutionSource = 'chatbot' | 'studio';
export type ExecutionType = 'simple' | 'chatflow' | 'workflow';
export type ExecutionStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

// Base execution interface
export interface AsyncExecution {
  execution_id: string;
  status: ExecutionStatus;
  progress: number;
  current_node: string | null;
  completed_nodes: number;
  total_nodes: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  final_output: Record<string, any> | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  execution_source: ExecutionSource;
  
  // Context-specific fields
  conversation_id?: string;  // For chat interface
  workspace_id?: string;     // For studio
  assistant_type?: ExecutionType;
  is_temporary?: boolean;
  cleanup_after?: string | null;
}

// =================== API Types ===================
export interface AsyncExecutionStartResponse {
  execution_id: string;
  status: string;
  message: string;
  estimated_duration_seconds?: number;
  position_in_queue?: number;
  cleanup_after?: string;
  status_url: string;
  stream_url: string;
  cancel_url: string;
}

export interface ExecutionStatusResponse {
  execution_id: string;
  status: ExecutionStatus;
  progress: number;
  current_node: string | null;
  completed_nodes: number;
  total_nodes: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  final_output: Record<string, any> | null;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  retry_count: number;
  execution_source: ExecutionSource;
  is_temporary: boolean;
  cleanup_after?: string | null;
}

export interface ExecutionCancelResponse {
  execution_id: string;
  cancelled: boolean;
  message: string;
  previous_status?: string;
}

// =================== Progress & Connection Types ===================
export interface ExecutionProgressEvent {
  type: 'status_update' | 'progress_event' | 'execution_finished' | 'stream_error';
  execution_id: string;
  status?: ExecutionStatus;
  progress?: number;
  current_node?: string | null;
  completed_nodes?: number;
  total_nodes?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  final_output?: Record<string, any> | null;
  error_message?: string | null;
  event_type?: string;
  node_id?: string;
  node_label?: string;
  message?: string;
  timestamp?: string;
  execution_source?: ExecutionSource;
}

export interface StreamConnection {
  executionId: string;
  status: 'connecting' | 'connected' | 'reconnecting' | 'failed' | 'stopped';
  attempts: number;
  lastError?: string;
  lastConnectedAt?: string;
  nextRetryAt?: string;
}

export interface ConnectionHealth {
  status: 'healthy' | 'degraded' | 'offline';
  activeConnections: number;
  failedConnections: number;
  lastFailureTime?: string;
  averageReconnectionTime: number;
}

// =================== Handler Types ===================
export interface AsyncExecutionHandlers {
  onExecutionStarted?: (execution: AsyncExecution) => void;
  onExecutionProgress?: (event: ExecutionProgressEvent) => void;
  onExecutionCompleted?: (execution: AsyncExecution) => void;
  onExecutionFailed?: (execution: AsyncExecution, error: string) => void;
  onExecutionCancelled?: (execution: AsyncExecution) => void;
  onConnectionUpdate?: (connection: StreamConnection) => void;
  onConnectionError?: (error: Error) => void;
}

// =================== Configuration ===================
export interface AsyncExecutionConfig {
  maxRetryAttempts: number;
  baseRetryDelay: number;
  maxRetryDelay: number;
  heartbeatInterval: number;
  connectionTimeout: number;
  enableAutoReconnect: boolean;
  defaultCleanupMinutes: number;
}

export const DEFAULT_ASYNC_CONFIG: AsyncExecutionConfig = {
  maxRetryAttempts: 10,
  baseRetryDelay: 1000,
  maxRetryDelay: 60000,
  heartbeatInterval: 30000,
  connectionTimeout: 90000,
  enableAutoReconnect: true,
  defaultCleanupMinutes: 60,
};

// =================== Utility Functions ===================
export function isExecutionActive(execution: AsyncExecution): boolean {
  return ['PENDING', 'RUNNING'].includes(execution.status);
}

export function isExecutionCompleted(execution: AsyncExecution): boolean {
  return ['COMPLETED', 'FAILED', 'CANCELLED'].includes(execution.status);
}

export function shouldUseAsyncExecution(type: ExecutionType): boolean {
  return type === 'chatflow' || type === 'workflow';
}